import React, { createElement, forwardRef, useEffect, useState } from "react";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  DataGridPro,
  DataGridProProps,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridApiPro,
  GridColDef,
  GridEventListener,
  GridProSlotsComponent,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponentsProps,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import { APIErrorResponse } from "shared/api/api";
import { APIFilter } from "shared/api/utils";
import { formatNumber } from "shared/locale/format";
import { SortBy, TestProps } from "shared/types";

import APIError from "features/ui/APIError";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { TableSearch } from "features/ui/Filters/types";
import ExpandIcon from "features/ui/Icons/ExpandIcon";
import SearchInput from "features/ui/SearchInput";

import CustomColumnMenu from "./components/CustomColumnMenu";
import CustomPaginationActions from "./components/CustomPaginationActions";
import FilterBuilderFilterPanel from "./components/FilterBuilderFilterPanel";
import {
  DEFAULT_GET_ROW_ID,
  DEFAULT_OVERLAY_HEIGHT,
  DEFAULT_PAGE_SIZE,
  DEFAULT_ROW_HEIGHT,
  NO_TOOLBAR,
} from "./constants";
import { convertFilterGroupStateToGridFilterModel } from "./filtering/utils";
import { useColumnPersistence } from "./hooks/useColumnPersistence";
import { useHorizontalOverflowScroll } from "./hooks/useHorizontalOverflowScroll";
import { useResetPageOnKeyChange } from "./hooks/useResetPageOnKeyChange";
import type { RowData, SchemaEntry } from "./types";
import {
  convertMaterialSortStateToSortBy,
  convertSchemaToMaterialColumnDefinition,
  convertSortByToMaterialSortState,
  getDataGridSx,
} from "./utils";

type GridProSubset = Pick<
  DataGridProProps,
  | "loading"
  | "hideFooter"
  | "rowCount"
  | "getRowId"
  | "disableColumnMenu"
  | "disableColumnSorting"
  | "rowHeight"
  | "getRowHeight"
  | "columnGroupingModel"
  | "checkboxSelection"
  | "checkboxColDef"
  | "rowSelectionModel"
  | "isRowSelectable"
>;

interface Props extends GridProSubset, TestProps {
  schema: SchemaEntry[];
  data?: RowData[];
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  pageSize?: number;
  sort?: SortBy;
  handleSorting?: (sort: SortBy) => void;
  formatCell?: (params: GridRenderCellParams) => any;
  filters?: FilterGroupState;
  staticFilters?: APIFilter[];
  hideStaticFiltersColumns?: boolean;
  filtersInitialized?: boolean;
  onFiltersReset?: (fieldNames?: string[]) => void;
  updateFilters?: (
    filters: FilterGroupState<FilterRowState>,
    callback?: (filters: FilterGroupState) => void,
    outsideChange?: boolean
  ) => void;
  renderPanel?: (params: GridRowParams) => React.ReactNode;
  defaultPanelExpanded?: boolean;
  hidePanelToggleColumn?: boolean;
  onPageChange?: (page: number) => void;
  toolbar?: React.JSXElementConstructor<any>;
  // Replaces the grid's own "no rows" text, so an empty table can carry a full empty state inside its own body.
  noRowsOverlay?: GridProSlotsComponent["noRowsOverlay"];
  overlayHeight?: number | string;
  // Drops the grid's frame, header fill, row rules, and hover and selection tints, for a table that reads as part
  // of the page. Selection stops being visible, so only pass it where the checkbox itself carries the meaning.
  borderless?: boolean;
  height?: string | number;
  apiRef?: React.RefObject<GridApiPro | null>;
  onPreferencePanelOpen?: () => void;
  onPreferencePanelClose?: () => void;
  page?: number;
  // A new value sends the grid back to page 0.
  resetKey?: string | null;
  hideTooltips?: boolean;
  underlineHeaderTooltips?: boolean;
  headerBackgroundColor?: string;
  pageKey: string; // Used for localStorage key for column order persistence
  pinnedLeftColumns?: string[];
  // The selection column is pinned by default, which puts it in front of the columns that scroll past it. Pass false
  // where it should scroll along with them instead.
  pinCheckboxColumn?: boolean;
  onRowClick?: (index: number) => void;
  ignoreRowClickOnCells?: string[];
  selectedRowIndex?: number;
  onRowMouseEnter?: (row: RowData) => void;
  onRowMouseLeave?: () => void;
  highlightedRowIds?: Set<string>;
  // Joined with the selection and highlight tints rather than replacing them.
  getRowClassName?: DataGridProProps["getRowClassName"];
  hideFooterRowCount?: boolean;
  onSelectionChange?: (model: GridRowSelectionModel) => void;
  processRowUpdate?: (newRow: GridRowModel) => GridRowModel;
  dynamicTableWidth?: boolean;
  maxTableWidth?: string;
  rowTestId?: string;
  // When true, the column header pins to the top of the nearest scrolling
  // ancestor.
  stickyHeader?: boolean;
  // When true, the pagination footer pins to the bottom of the nearest
  // scrolling ancestor.
  stickyFooter?: boolean;
  // Pixel offset for the sticky column-header row (e.g. when the table is
  // nested inside a Smart Group detail panel and the outer header occupies
  // the top of the viewport).
  stickyTopOffset?: number;
  search?: TableSearch;
  error?: APIErrorResponse;
  onBadRequest?: () => void;
}

const MaterialTable = ({
  schema,
  data = [],
  getRowId = DEFAULT_GET_ROW_ID,
  onNextPage,
  onPreviousPage,
  hasNextPage,
  hasPreviousPage,
  pageSize = DEFAULT_PAGE_SIZE,
  rowCount,
  sort,
  handleSorting,
  formatCell,
  loading,
  hideFooter,
  filters,
  updateFilters,
  renderPanel,
  defaultPanelExpanded = false,
  hidePanelToggleColumn = false,
  disableColumnMenu = false,
  disableColumnSorting = false,
  onPageChange,
  toolbar,
  noRowsOverlay,
  overlayHeight = noRowsOverlay ? DEFAULT_OVERLAY_HEIGHT : undefined,
  height,
  apiRef,
  rowHeight = DEFAULT_ROW_HEIGHT,
  getRowHeight,
  onPreferencePanelOpen,
  onPreferencePanelClose,
  page,
  resetKey,
  hideTooltips = false,
  underlineHeaderTooltips = false,
  headerBackgroundColor,
  pageKey,
  columnGroupingModel,
  pinnedLeftColumns,
  onRowClick,
  ignoreRowClickOnCells = [],
  selectedRowIndex,
  onRowMouseEnter,
  onRowMouseLeave,
  highlightedRowIds,
  getRowClassName,
  hideFooterRowCount = false,
  checkboxSelection = false,
  checkboxColDef,
  pinCheckboxColumn = true,
  onSelectionChange,
  rowSelectionModel,
  isRowSelectable,
  processRowUpdate,
  dynamicTableWidth = false,
  maxTableWidth = "100%",
  rowTestId,
  testId,
  borderless = false,
  stickyHeader = false,
  stickyFooter = false,
  stickyTopOffset,
  search,
  error,
  onBadRequest,
}: Props) => {
  const { t } = useTranslation("common");
  const anySticky = stickyHeader || stickyFooter;
  const [pageSizeOptions] = useState([pageSize]);
  const [paginationModel, setPaginationModel] = useState({
    page: page || 0,
    pageSize,
  });

  // Create internal apiRef for column persistence if not provided externally
  const internalApiRef = useGridApiRef();
  const effectiveApiRef = apiRef || internalApiRef;

  const onPaginationChange = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
    onPageChange?.(model.page);
  };

  useResetPageOnKeyChange(resetKey, (page) =>
    onPaginationChange({ ...paginationModel, page })
  );

  const filterModel = convertFilterGroupStateToGridFilterModel(filters, schema);

  const [sortModel, setSortModel] = useState<GridSortModel>(
    convertSortByToMaterialSortState(sort)
  );

  const baseColumns: GridColDef[] = convertSchemaToMaterialColumnDefinition(
    schema,
    formatCell,
    hideTooltips,
    underlineHeaderTooltips
  );

  const { columns, handleColumnOrderChange, handleColumnWidthChange } =
    useColumnPersistence(baseColumns, { pageKey, apiRef: effectiveApiRef });

  // Phantom column absorbs leftover space so the last real column always has
  // a right neighbour to drag against when resizing. flex:1 + minWidth:0 means
  // it collapses to zero when columns already fill the container.
  const columnsWithPhantom: GridColDef[] = [
    ...columns,
    {
      field: "__phantom__",
      headerName: "",
      flex: 1,
      minWidth: 0,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      resizable: false,
      renderCell: () => null,
      renderHeader: () => null,
    },
  ];

  const applyFilters = (filterState: FilterGroupState) => {
    updateFilters?.(filterState);
  };

  const {
    containerRef: hScrollContainerRef,
    scrollbarRef: hScrollbarRef,
    scrollbarProps: hScrollbarProps,
    showScrollbar: showHScrollbar,
    totalWidth: hScrollTotalWidth,
    footerHeight: hScrollFooterHeight,
    isHeaderFullyVisible: hScrollHeaderVisible,
  } = useHorizontalOverflowScroll(anySticky, effectiveApiRef);

  const sx = getDataGridSx({
    height,
    loading,
    headerBackgroundColor,
    isRowClickable: !!onRowClick,
    stickyHeader,
    // The footer only pins once the column header is fully in view — this
    // avoids the footer overlapping the header when the grid is entering
    // the viewport from the bottom.
    stickyFooter: stickyFooter && hScrollHeaderVisible,
    stickyTopOffset,
    pageSize,
    rowHeight,
    overlayHeight,
    borderless,
    renderedRowCount: data.length,
  });

  const slotProps: GridSlotsComponentsProps = {
    basePagination: {
      material: {
        ActionsComponent: (props) => (
          <CustomPaginationActions
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPreviousPage}
            {...props}
          />
        ),
        labelDisplayedRows:
          hideFooterRowCount || !rowCount
            ? () => null
            : ({ from, to, count }) =>
                count > -1 ? (
                  <Trans
                    t={t}
                    i18nKey="material_table.displayed_rows"
                    values={{ from, to, count: formatNumber(count) }}
                    components={{
                      total: <span data-testid="table-total-count" />,
                    }}
                  />
                ) : null,
      },
    },
    // Use fixed positioning so the filter panel tracks the sticky header anchor
    // instead of drifting when the page scrolls. Sent via basePopper because
    // GridPanel merges slotProps.basePopper onto the underlying MUI Popper.
    ...(stickyHeader && {
      basePopper: {
        popperOptions: { strategy: "fixed" as const },
      },
    }),
    filterPanel: {
      filterGroupState: filters,
      filtersSchema: schema.filter((s) => s.filter && !s.hideFilter),
      onApplyFilters: applyFilters,
    },
    loadingOverlay: {
      variant: "skeleton",
      noRowsVariant: "skeleton",
    },
    // The grid drops props it does not know, so the test id has to reach its root through the slot.
    ...(testId && { root: { "data-testid": testId } }),
    ...(rowTestId && {
      row: {
        "data-testid": rowTestId,
      },
    }),
  };

  const slots: Partial<GridProSlotsComponent> = {
    columnMenu: CustomColumnMenu,
    ...(noRowsOverlay && { noRowsOverlay }),
    filterPanel: FilterBuilderFilterPanel,
    detailPanelExpandIcon: () => <ExpandIcon />,
    detailPanelCollapseIcon: () => <ExpandLessIcon />,
    toolbar: toolbar
      ? forwardRef((props, ref) => createElement(toolbar, { ...props, ref }))
      : NO_TOOLBAR,
  };

  useEffect(() => {
    setPaginationModel({
      page: page || 0,
      pageSize,
    });
  }, [page, pageSize]);

  useEffect(() => {
    if (!onRowMouseEnter && !onRowMouseLeave) return;

    if (!effectiveApiRef.current) return;

    const api = effectiveApiRef.current;
    const unsubEnter = onRowMouseEnter
      ? api.subscribeEvent("rowMouseEnter", (params) => {
          onRowMouseEnter(params.row as RowData);
        })
      : undefined;
    const unsubLeave = onRowMouseLeave
      ? api.subscribeEvent("rowMouseLeave", () => {
          onRowMouseLeave();
        })
      : undefined;

    return () => {
      unsubEnter?.();
      unsubLeave?.();
    };
  }, [onRowMouseEnter, onRowMouseLeave, effectiveApiRef]);

  const handleRowClick: GridEventListener<"rowClick"> = (params, event) => {
    if (onRowClick && effectiveApiRef.current) {
      // some fields have their own on-click functionality - we want to skip them
      const target = event.target as HTMLElement;
      const cell = target.closest("[data-field]");
      const field = cell?.getAttribute("data-field");

      if (field && ignoreRowClickOnCells.includes(field)) {
        return;
      }

      const index = effectiveApiRef.current.getRowIndexRelativeToVisibleRows(
        params.id
      );
      onRowClick(index);
    }
  };

  // always remove borders from column headers so we dont have double-borders visible
  const columnGroupingModelModified =
    columnGroupingModel &&
    columnGroupingModel.map((item) => ({
      ...item,
      headerClassName: "!border-none",
    }));

  const editable = schema.some(({ editable }) => editable);

  const totalColumnWidth = columns.reduce(
    (sum, col) => sum + (col.width || 100),
    0
  );

  const searchBox = search && (
    <div className="mb-3">
      <SearchInput
        searchTerm={search.value}
        onChange={search.onChange}
        testId={`${pageKey}-search`}
      />
    </div>
  );

  if (error) {
    return (
      <>
        {searchBox}
        <APIError error={error} onBadRequest={onBadRequest} />
      </>
    );
  }

  const dataGrid = (
    <DataGridPro
      autoHeight
      getRowClassName={(params) => {
        const isSelected =
          params.indexRelativeToCurrentPage === selectedRowIndex;

        return classNames(getRowClassName?.(params), {
          "!bg-blue-100": isSelected,
          "!bg-blue-50":
            !isSelected && highlightedRowIds?.has(String(params.id)),
        });
      }}
      apiRef={effectiveApiRef}
      density="compact"
      rows={data}
      columns={columnsWithPhantom}
      className="normal-table"
      hideFooter={hideFooter}
      disableVirtualization
      getRowHeight={getRowHeight || (() => rowHeight)}
      sx={sx}
      getRowId={getRowId}
      paginationMode="server"
      sortingMode="server"
      filterMode="server"
      pageSizeOptions={pageSizeOptions}
      paginationModel={paginationModel}
      sortingOrder={["desc", "asc"]}
      sortModel={sortModel}
      pagination
      loading={loading}
      disableRowSelectionOnClick
      disableMultipleColumnsSorting
      disableColumnMenu={disableColumnMenu}
      disableColumnSorting={disableColumnSorting}
      filterModel={filterModel}
      slotProps={slotProps}
      slots={slots}
      showToolbar={!!toolbar}
      rowCount={rowCount !== undefined ? rowCount : -1}
      onPaginationModelChange={onPaginationChange}
      onSortModelChange={(sortingModel) => {
        if (JSON.stringify(sortModel) === JSON.stringify(sortingModel)) return;

        setSortModel(sortingModel);
        handleSorting?.(convertMaterialSortStateToSortBy(sortingModel));
      }}
      getDetailPanelContent={renderPanel}
      getDetailPanelHeight={() => "auto"}
      detailPanelExpandedRowIds={
        defaultPanelExpanded
          ? new Set(data.map((row) => getRowId(row)))
          : undefined
      }
      columnVisibilityModel={{
        ...(hidePanelToggleColumn && {
          [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
        }),
      }}
      columnGroupingModel={columnGroupingModelModified}
      initialState={{
        pinnedColumns: {
          left: [
            GRID_DETAIL_PANEL_TOGGLE_FIELD,
            ...(pinCheckboxColumn ? [GRID_CHECKBOX_SELECTION_FIELD] : []),
            ...(pinnedLeftColumns || []),
          ],
        },
      }}
      onPreferencePanelOpen={onPreferencePanelOpen}
      onPreferencePanelClose={onPreferencePanelClose}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
      onRowClick={handleRowClick}
      checkboxSelection={checkboxSelection}
      checkboxColDef={checkboxColDef}
      onRowSelectionModelChange={onSelectionChange}
      rowSelectionModel={rowSelectionModel}
      isRowSelectable={isRowSelectable}
      editMode={editable ? "row" : undefined}
      processRowUpdate={processRowUpdate}
    />
  );

  const table = (
    <div
      style={
        dynamicTableWidth
          ? {
              width: totalColumnWidth + 10, // add some extra space for padding and scrollbar
              maxWidth: maxTableWidth,
              position: "relative",
              left: "50%",
              transform: "translateX(-50%)",
            }
          : {}
      }
    >
      {anySticky ? <div ref={hScrollContainerRef}>{dataGrid}</div> : dataGrid}
      {anySticky && showHScrollbar && hScrollHeaderVisible && (
        <div
          ref={hScrollbarRef}
          {...hScrollbarProps}
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            zIndex: 3,
            backgroundColor: "transparent",
            // Pin the scrollbar to the viewport only when the footer itself
            // is sticky (non-nested). Placing it in natural flow just below
            // the grid avoids a visual gap below the column headers once
            // sticky releases, and keeps the nested footer visible.
            ...(stickyFooter
              ? { position: "sticky", bottom: hScrollFooterHeight }
              : {}),
          }}
          data-testid="material-table-horizontal-scrollbar"
        >
          <div
            style={{
              width: hScrollTotalWidth,
              height: "10px",
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      {searchBox}
      {table}
    </>
  );
};

export default MaterialTable;
